/**
 * Support tickets — types, labels, helpers used by both the user-facing
 * "Reportar problema" flow and the admin panel.
 */

import { supabase } from '@/integrations/supabase/client';

export type TicketStatus = 'open' | 'in_progress' | 'resolved' | 'need_info' | 'on_hold';
export type TicketCategory = 'bug' | 'suggestion' | 'question' | 'payment' | 'other';
export type TicketPriority = 'P1' | 'P2' | 'P3' | 'P4';

export interface SupportTicket {
  id: number;
  ticket_number: string;
  user_id: string;
  category: TicketCategory;
  title: string;
  description: string | null;
  status: TicketStatus;
  priority: TicketPriority;
  context_url: string | null;
  context_user_agent: string | null;
  context_app_version: string | null;
  context_user_role: string | null;
  context_user_email: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  /** Cuándo vence la primera respuesta del equipo (calculado desde
   *  priority + created_at por trigger). Null para tickets viejos
   *  sin SLA asignado. */
  sla_due_at: string | null;
  /** Cuándo el primer mensaje de admin/support se registró. Se setea
   *  automáticamente por trigger al insertar mensaje con role admin
   *  o support. Null = todavía no hay primera respuesta. */
  responded_at: string | null;
  /** Timestamp cuando se pausó. Null si no está pausado actualmente.
   *  Mientras paused_at != null, el SLA está "congelado" — el frontend
   *  oculta el countdown y muestra "En pausa desde X". */
  paused_at: string | null;
  /** Razón de la pausa visible al cliente. Null si no está pausado. */
  pause_reason: string | null;
  /** Timestamp del último botón "Solicitar status" presionado por el
   *  cliente cuando el ticket lleva tiempo en pausa. Cooldown 24h. */
  last_user_status_request_at: string | null;
  /** Timestamp del último botón "Solicitar respuesta urgente" cuando
   *  el SLA venció sin respuesta. Cooldown 24h. */
  last_user_escalation_at: string | null;
  /** Cuándo el agente IA tocó este ticket (resolvió o escaló). NULL = pendiente. */
  ai_processed_at: string | null;
  /** 0.00-1.00. ≥0.70 = el agente resolvió. <0.70 = escaló a humano. */
  ai_confidence: number | null;
  /** Acción que tomó el agente IA. NULL = aún no procesado. */
  ai_action: 'resolved' | 'escalated' | 'skipped' | 'failed' | null;
}

export interface TicketMessage {
  id: number;
  ticket_id: number;
  /** NULL cuando el mensaje lo escribió el agente IA "Insane Force Team
   *  Support" (combinado con is_ai_generated=true). */
  author_id: string | null;
  message: string;
  is_internal: boolean;
  created_at: string;
  /** True cuando el mensaje lo escribió el agente IA. La UI lo muestra
   *  como "Insane Force Team Support" con badge 🤖. */
  is_ai_generated: boolean;
}

export interface TicketAttachment {
  id: number;
  ticket_id: number;
  message_id: number | null;
  storage_path: string;
  file_type: string | null;
  file_size: number | null;
  uploaded_by: string;
  created_at: string;
  /** Computed at fetch time — signed URL valid 1 hour. */
  signed_url?: string;
}

export const CATEGORY_META: Record<TicketCategory, { label: string; emoji: string }> = {
  bug:        { label: 'Problema',          emoji: '⚠️' },
  suggestion: { label: 'Sugerencia',        emoji: '💡' },
  question:   { label: 'Pregunta',          emoji: '❓' },
  payment:    { label: 'Problema con pago', emoji: '💳' },
  other:      { label: 'Otro',              emoji: '📋' },
};

export const STATUS_META: Record<TicketStatus, { label: string; emoji: string; classes: string }> = {
  open:        { label: 'Abierto',     emoji: '🔵', classes: 'bg-blue-500/15 text-blue-700 dark:text-blue-400 border-blue-500/30' },
  in_progress: { label: 'En revisión', emoji: '🟡', classes: 'bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30' },
  need_info:   { label: 'Pide info',   emoji: '🟠', classes: 'bg-orange-500/15 text-orange-700 dark:text-orange-400 border-orange-500/30' },
  on_hold:     { label: 'En pausa',    emoji: '⏸',  classes: 'bg-purple-500/15 text-purple-700 dark:text-purple-400 border-purple-500/30' },
  resolved:    { label: 'Resuelto',    emoji: '🟢', classes: 'bg-green-500/15 text-green-700 dark:text-green-400 border-green-500/30' },
};

/**
 * Upload a file to support-attachments and link it to a ticket
 * (and optionally to a specific message). Path layout:
 *   {user_id}/ticket-{ticket_id}/{timestamp}-{random}.{ext}
 *
 * Storage RLS scopes uploads by user_id folder, so the user can
 * upload only under their own path; admin can read everything.
 */
export async function uploadTicketAttachment(
  ticketId: number,
  file: File,
  messageId?: number,
): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Sesión expirada');

  const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
  const path = `${user.id}/ticket-${ticketId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;

  const up = await supabase.storage.from('support-attachments').upload(path, file, {
    cacheControl: '3600',
    upsert: false,
    contentType: file.type,
  });
  if (up.error) throw new Error('No se pudo subir archivo: ' + up.error.message);

  const ins = await supabase.from('support_ticket_attachments').insert({
    ticket_id: ticketId,
    message_id: messageId ?? null,
    storage_path: path,
    file_type: file.type,
    file_size: file.size,
    uploaded_by: user.id,
  });
  if (ins.error) throw new Error('Subido pero no enlazado: ' + ins.error.message);
}

/**
 * Build signed URLs for a list of attachments. Skips silently on errors
 * so the UI never breaks because one file is missing.
 */
export async function signAttachments(
  attachments: TicketAttachment[],
  ttlSeconds = 3600,
): Promise<TicketAttachment[]> {
  if (attachments.length === 0) return attachments;
  const paths = attachments.map(a => a.storage_path);
  const { data, error } = await supabase
    .storage.from('support-attachments')
    .createSignedUrls(paths, ttlSeconds);
  if (error || !data) return attachments;
  return attachments.map((a, i) => ({ ...a, signed_url: data[i]?.signedUrl }));
}

/** Capture page context to attach to every ticket — saves users from
 *  having to describe what they were doing. */
export function captureContext(): {
  context_url: string;
  context_user_agent: string;
  context_app_version: string;
} {
  return {
    context_url: typeof window !== 'undefined' ? window.location.href : '',
    context_user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    // Hardcoded for now — bump on each release. Could read from
    // package.json at build time later.
    context_app_version: '1.0.0',
  };
}
